<template>
  <TpReviews class="above-xl-hide" />

  <div
    class="season-countdown"
    @mouseenter="showDetail"
    @mouseleave="hideDetail"
  >
    <div class="progress-container">
      <svg class="progress-circle" width="40" height="40" viewBox="0 0 40 40">
        <circle
          class="progress-background"
          cx="20"
          cy="20"
          r="16"
          fill="none"
          stroke="#333333"
          stroke-width="3"
        />
        <circle
          class="progress-indicator"
          cx="20"
          cy="20"
          r="16"
          fill="none"
          stroke="#FF8C00"
          stroke-width="3"
          stroke-dasharray="100"
          :stroke-dashoffset="completionPercentage"
          transform="rotate(-90 20 20)"
        />
      </svg>
    </div>
    <div class="d-flex-column justify-start align-start gap-1">
      <h2 class="bold-xl font-family-2 t-white">{{ seasonLength?.title }}</h2>
      <p class="subtitle font-family-2 t-grey caps medium-s">
        {{ remaningDays }} {{ $t("days_left") }}
      </p>
    </div>
    <div class="info-icon">
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <circle cx="12" cy="12" r="10" stroke="#666666" stroke-width="2" />
        <path
          d="M12 7V12"
          stroke="#666666"
          stroke-width="2"
          stroke-linecap="round"
        />
        <circle cx="12" cy="16" r="1" fill="#666666" />
      </svg>
    </div>

    <div class="bg_blur"></div>

    <Transition
      name="opacity"
      enter-active-class="season_length_anim_in"
      leave-active-class="season_length_anim_out"
    >
      <div class="season_countdown_hover_container b-radius-10" v-if="isOpen">
        <div class="season_length_container b-radius-6">
          <SeasonLength :season-length="seasonLength" />
        </div>
        <p class="t-grey regular-m font-family-2" style="padding: 0 16px">
          {{ $t("season_length_description") }}
        </p>
      </div>
    </Transition>
  </div>
</template>

<script setup>
import SeasonLength from "./SeasonLength.vue";
import TpReviews from "../common/sections/boosting/TpReviews.vue";

const props = defineProps({
  seasonLength: {
    default: {},
  },
});
const isOpen = ref(false);
const hideTimeout = ref(null);
// Replace the click handler with mouse handlers
const showDetail = () => {
  clearTimeout(hideTimeout.value);
  isOpen.value = true;
};

const hideDetail = () => {
  // Add a small delay before hiding to prevent flickering
  hideTimeout.value = setTimeout(() => {
    isOpen.value = false;
  }, 100);
};

const clearHideTimeout = () => {
  clearTimeout(hideTimeout.value);
  isOpen.value = true;
};

const handleDetailLeave = () => {
  hideTimeout.value = setTimeout(() => {
    isOpen.value = false;
  }, 100);
};

const numberOfSeasonDays = computed(() => {
  if (!props.seasonLength?.id) return 0;
  const startDate = new Date(props.seasonLength?.start_at);
  const endDate = new Date(props.seasonLength?.end_at);

  const timeDifference = endDate - startDate;
  const daysDifference = timeDifference / (1000 * 60 * 60 * 24);

  return daysDifference;
});

const remaningDays = computed(() => {
  if (!props.seasonLength?.id) return 0;

  const endDate = new Date(props.seasonLength.end_at);
  const currentDate = new Date();

  if (endDate < currentDate) return 0;

  const timeDifference = endDate.getTime() - currentDate.getTime();
  const daysDifference = timeDifference / (1000 * 60 * 60 * 24);

  return Math.ceil(daysDifference); // Round up to include today
});

const passedDays = computed(() => {
  if (!props.seasonLength?.id) return 0;
  const startDate = new Date(props.seasonLength?.start_at);
  const endDate = new Date();

  const timeDifference = endDate - startDate;
  const daysDifference = Math.floor(timeDifference / (1000 * 60 * 60 * 24));
  if (daysDifference < 0) {
    return 0;
  }
  if (daysDifference > numberOfSeasonDays.value) {
    return numberOfSeasonDays.value;
  }
  return daysDifference;
});

const completionPercentage = computed(() => {
  const pr = (passedDays.value / numberOfSeasonDays.value) * 100;

  return 100 - Math.ceil(pr);
});
</script>

<style lang="scss" scoped>
.season-countdown {
  display: flex;
  width: 100%;
  align-items: center;
  background-color: var(--navigation-opace);
  border-radius: 10px;
  padding: 12px 16px;
  color: var(--text-white);
  position: relative;
  cursor: pointer;
  & > * {
    z-index: 1;
  }

  .bg_blur {
    z-index: 0;
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    overflow: hidden;
    width: 100%;
    &::before {
      border-radius: 10px;
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      opacity: 0.08;
      background: var(--Orange, #ff9e48);
      height: 100%;
      width: 40%;
      filter: blur(30px);
    }
  }
  .progress-container {
    position: relative;
    width: 40px;
    height: 40px;
    margin-right: 12px;

    .progress-circle {
      .progress-background {
        opacity: 0.3;
      }

      .progress-indicator {
        transition: stroke-dashoffset 0.5s ease;
      }
    }
  }

  .text-container {
    display: flex;
    flex-direction: column;
    justify-content: center;
  }

  .info-icon {
    margin-left: auto;
    cursor: pointer;
    opacity: 0.7;
    transition: opacity 0.2s ease;

    &:hover {
      opacity: 1;
    }
  }

  .season_countdown_hover_container {
    width: 100%;
    position: absolute;
    // height: 100%;

    display: inline-flex;
    padding: 4px 4px 15px 4px;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 8px;
    border: 1px solid rgba(255, 255, 255, 0.1);
    background: var(--bg-main);
    box-shadow: 0px 4px 20px 0px rgba(5, 6, 7, 0.8);
    backdrop-filter: blur(4.550000190734863px);
    bottom: 110%;
    left: 0;
    .season_length_container {
      width: 100%;
      height: max-content;
      padding: 8px;
      background-color: var(--bg-dark);
    }
  }
}
</style>
